import { range, shuffle } from "d3-array";

const graphGenerator = (populationSize, immunoCompromisedSize) => {
  const elders = [];
  const adults = [];
  const children = [];

  function randomMember(group) {
    return group[Math.floor(Math.random() * group.length)];
  }

  function randomAdult() {
    return randomMember(adults);
  }

  function randomElder() {
    return randomMember(elders);
  }

  function randomChild() {
    return randomMember(children);
  }

  const immunoCompromised = range(immunoCompromisedSize).map(i => {
    const p = {
      id: "i" + i,
      age:
        Math.random() > 0.5 ? "elder" : Math.random() > 0.5 ? "child" : "adult",
      immuno: true
    };

    switch (p.age) {
      case "child":
        children.push(p);
        break;
      case "adult":
        adults.push(p);
        break;
      default:
        elders.push(p);
    }

    return p;
  });

  const nodes = range(populationSize - immunoCompromisedSize).map(i => {
    const age = Math.random() * 85;

    const p = {
      id: i,
      age: age < 14 ? "child" : age > 65 ? "elder" : "adult",
      immuno: false
    };

    switch (p.age) {
      case "child":
        children.push(p);
        break;
      case "adult":
        adults.push(p);
        break;
      default:
        elders.push(p);
    }

    return p;
  });

  adults[0].id = "a";
  adults[0].fx = 400;
  adults[0].fy = 400;

  const population = shuffle(nodes.concat(immunoCompromised));

  function randomPerson() {
    return randomMember(population);
  }

  const links = [];

  let parents = [randomAdult(), randomAdult()];
  let sibings = [];

  children.forEach((n, i) => {
    if (Math.random() > 0.9) {
      // Sibling with the previous child
      parents.forEach(parent => {
        links.push({
          source: n.id,
          target: parent.id,
          priority: "IMMEDIATE-FAMILY"
        });
      });

      sibings.forEach(s => {
        links.push({
          source: n.id,
          target: s.id,
          priority: "IMMEDIATE-FAMILY"
        });
      });

      sibings.push(n);
    } else {
      // Child
      if (Math.random() > 0.6) {
        parents = [randomAdult()];
        links.push({
          source: n.id,
          target: parents[0].id,
          priority: "IMMEDIATE-FAMILY"
        });
      } else {
        parents = [randomAdult(), randomAdult()];
        links.push({
          source: n.id,
          target: parents[0].id,
          priority: "IMMEDIATE-FAMILY"
        });
        links.push({
          source: n.id,
          target: parents[1].id,
          priority: "IMMEDIATE-FAMILY"
        });

        links.push({
          source: parents[0].id,
          target: parents[1].id,
          priority: "IMMEDIATE-FAMILY"
        });
      }

      sibings = [n];
    }

    // Grand Parents
    if (Math.random() > 0.5) {
      links.push({
        source: n.id,
        target: randomElder().id,
        priority: "FAMILY"
      });
    }
  });

  elders.forEach(n => {
    links.push({
      source: n.id,
      target: randomAdult().id,
      priority: "FAMILY"
    });

    if (Math.random() > 0.4) {
      links.push({
        source: n.id,
        target: randomPerson().id,
        priority: "FRIENDS"
      });
    }
  });

  const nonEssentialServiceWorker = [];
  const essentialServiceWorkers = [];
  const smallWorkClusters = [];
  const largerWorkClusters = [];

  adults.forEach((n, i) => {
    // Social Connections
    // At least one
    const numOfSocialSocialContacts = Math.max(
      0,
      Math.floor(Math.pow(1 + Math.random() / 2, 2))
    );

    range(numOfSocialSocialContacts).forEach(j => {
      links.push({
        source: n.id,
        target: randomAdult().id,
        priority: "FRIENDS"
      });
    });

    const workplace = Math.random();

    if (workplace < 0.2) {
      // Service work – connected to large number of people, but low priority
      nonEssentialServiceWorker.push(n);
    } else if (workplace < 0.3) {
      essentialServiceWorkers.push(n);
    } else if (workplace < 0.8) {
      smallWorkClusters.push(n);
    } else {
      largerWorkClusters.push(n);
    }
  });

  const essentialFrequency = 3;
  essentialServiceWorkers.forEach(n => {
    let i = 0;
    while (i < adults.length) {
      links.push({
        source: n.id,
        target: adults[i].id,
        priority: "ESSENTIAL-WORK"
      });

      i += Math.floor((Math.random() * adults.length) / essentialFrequency);
    }
  });

  const nonEssentialFrequency = 6;
  nonEssentialServiceWorker.forEach(n => {
    let i = Math.floor((Math.random() * adults.length) / nonEssentialFrequency);
    while (i < adults.length) {
      links.push({
        source: n.id,
        target: adults[i].id,
        priority: "NON-ESSENTIAL-WORK"
      });

      i += Math.floor((Math.random() * adults.length) / nonEssentialFrequency);
    }
  });

  // Other Workplaces
  const groupIterator = members => {
    if (members.length <= 1) {
      return;
    }

    const n = members[0];
    const rest = members.slice(1);
    rest.forEach(m => {
      links.push({
        source: n.id,
        target: m.id,
        priority: "NON-ESSENTIAL-WORK"
      });
    });

    groupIterator(rest);
  };

  let swcIncrement = 0;
  while (swcIncrement < smallWorkClusters.length) {
    const groupSize = Math.min(
      Math.ceil(Math.random() * 5) + 1,
      smallWorkClusters.length - swcIncrement
    );

    const members = range(groupSize).map(i => {
      return smallWorkClusters[i + swcIncrement];
    });
    groupIterator(members);

    swcIncrement += groupSize + 1;
  }

  let lwcIncrement = 0;
  while (lwcIncrement < largerWorkClusters.length) {
    const groupSize = Math.min(
      Math.ceil(Math.random() * 6) + 9,
      largerWorkClusters.length - lwcIncrement
    );

    const members = range(groupSize).map(i => {
      return largerWorkClusters[i + lwcIncrement];
    });
    groupIterator(members);

    lwcIncrement += groupSize + 1;
  }

  return {
    nodes: population,
    links: links
  };
};

export default graphGenerator;
